import * as React from 'react'

import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'

import { useDispatch, useSelector } from 'react-redux';

import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';

import { CTextField } from '../../components/common/TextField';
import { CPageButton } from '../../components/common/Button';

import UserModal from '../../modals/admin/UserModal';
import { getUsers, deleteUser, resetPassword } from '../../slices/user';

const roleConfig = {
  SUPER_ADMIN: { label: 'SUPER ADMIN', bg: '#ede9fe', color: '#5b21b6' },
  ADMIN:       { label: 'ADMIN',       bg: '#dcfce7', color: '#15803d' },
  USER:        { label: 'USER',        bg: '#fff7ed', color: '#b45309' },
};

const avatarColors = [
  '#667eea', '#764ba2', '#06b6d4', '#10b981', '#f59e0b', '#ef4444',
];

function getAvatarColor(email) {
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

function User() {
  const [openNewUser, setOpenNewUser] = React.useState(false);
  const [mode, setMode] = React.useState('Create');
  const [userData, setUserData] = React.useState({ email: '', firstname: '', lastname: '', role: '', id: '' });
  const [search, setSearch] = React.useState('');
  const [resetTargetId, setResetTargetId] = React.useState(null);
  const [rowHeight, setRowHeight] = React.useState(60);
  const tableContainerRef = React.useRef(null);

  const dispatch = useDispatch();
  const users = useSelector(state => state.user.users);
  const pagination = useSelector(state => state.user.pagination);

  React.useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const calc = () => {
      const thead = el.querySelector('thead tr');
      const headerH = thead ? thead.getBoundingClientRect().height : 40;
      setRowHeight(Math.floor((el.clientHeight - headerH) / 10));
    };
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fetchPage = React.useCallback((p) => {
    const formData = new FormData();
    formData.append('page', p);
    dispatch(getUsers(formData));
  }, [dispatch]);

  React.useEffect(() => { fetchPage(1); }, [fetchPage]);

  const filtered = search
    ? users.filter(u => u.email.toLowerCase().includes(search.toLowerCase()))
    : users;

  const handleOpenNewUser = () => {
    setUserData({ id: '', email: '', firstname: '', lastname: '', role: '' });
    setMode('Create');
    setOpenNewUser(true);
  };

  const handleCloseNewUser = () => {
    setOpenNewUser(false);
    fetchPage(pagination.page);
  };

  const handleEdit = (user) => {
    const { id, email, role, first_name: firstname, last_name: lastname } = user;
    setMode('Update');
    setUserData({ id, email, firstname, lastname, role });
    setOpenNewUser(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Do you really want to remove this user?')) {
      dispatch(deleteUser(id)).catch(err => console.log(err));
    }
  };

  const handleConfirmReset = () => {
    dispatch(resetPassword(resetTargetId)).catch(err => console.log(err));
    setResetTargetId(null);
  };

  const { page, total_pages } = pagination;
  const pageNumbers = React.useMemo(() => {
    const arr = [];
    if (total_pages <= 7) {
      for (let i = 1; i <= total_pages; i++) arr.push(i);
    } else {
      arr.push(1);
      if (page > 3) arr.push('...');
      for (let i = Math.max(2, page - 1); i <= Math.min(total_pages - 1, page + 1); i++) arr.push(i);
      if (page < total_pages - 2) arr.push('...');
      arr.push(total_pages);
    }
    return arr;
  }, [page, total_pages]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#f4f5fb', p: '28px', gap: '16px' }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: '12px',
            background: '#3b82f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(59,130,246,0.3)',
          }}>
            <PeopleAltOutlinedIcon sx={{ color: '#fff', fontSize: '20px' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
              User Management
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: '1px' }}>
              {users.length} registered user{users.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>

        <CTextField
          size="small"
          placeholder="Search by email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{
            width: 280,
            background: '#fff',
            '& .MuiInputBase-root': { height: 36, fontSize: '0.85rem' },
            '& .MuiInputBase-input': { py: 0 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: '18px' }} />}
          onClick={handleOpenNewUser}
          sx={{
            background: '#3b82f6',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem',
            borderRadius: '8px',
            px: '16px',
            height: 36,
            flexShrink: 0,
            ml: 'auto',
            boxShadow: '0 2px 8px rgba(59,130,246,0.35)',
            '&:hover': {
              background: '#2563eb',
              boxShadow: '0 4px 12px rgba(59,130,246,0.4)',
            },
          }}
        >
          Add New User
        </Button>
      </Box>

      {/* Table Card */}
      <Paper sx={{
        flex: 1,
        minHeight: 0,
        borderRadius: '16px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
        border: '1px solid #e4e7f0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <TableContainer ref={tableContainerRef} sx={{ flex: 1, overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} size="small">
            <TableHead>
              <TableRow>
                {['Email', 'Role', 'Created', 'Actions'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      color: '#94a3b8',
                      py: '10px',
                      background: '#fff',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 10 }).map((_, i) => {
                const user = filtered[i];
                return (
                  <TableRow
                    key={user ? user.id : `empty-${i}`}
                    sx={{
                      height: rowHeight,
                      '&:last-child td': { border: 0 },
                      '&:hover td': user ? { background: '#f8f9ff' } : {},
                      transition: 'background 0.12s ease',
                    }}
                  >
                    {user ? (
                      <>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Avatar
                              sx={{
                                width: 34, height: 34,
                                fontSize: '0.8rem', fontWeight: 700,
                                background: getAvatarColor(user.email),
                                flexShrink: 0,
                              }}
                            >
                              {user.email[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b' }}>
                                {user.email}
                              </Typography>
                              {(user.first_name || user.last_name) && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                  {[user.first_name, user.last_name].filter(Boolean).join(' ')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell>
                          {(() => {
                            const cfg = roleConfig[user.role] || roleConfig.USER;
                            return (
                              <Box component="span" sx={{
                                display: 'inline-block',
                                px: '10px', py: '3px',
                                borderRadius: '20px',
                                background: cfg.bg,
                                color: cfg.color,
                                fontSize: '0.7rem', fontWeight: 700,
                                letterSpacing: '0.05em',
                              }}>
                                {cfg.label}
                              </Box>
                            );
                          })()}
                        </TableCell>

                        <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>
                          {user.created_at}
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', gap: '4px' }}>
                            <Tooltip title="Edit user" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(user)}
                                sx={{ color: '#667eea', borderRadius: '8px', '&:hover': { background: '#ede9fe', color: '#5b21b6' } }}
                              >
                                <EditOutlinedIcon sx={{ fontSize: '17px' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset password" arrow>
                              <IconButton
                                size="small"
                                onClick={() => setResetTargetId(user.id)}
                                sx={{ color: '#f59e0b', borderRadius: '8px', '&:hover': { background: '#fef3c7', color: '#d97706' } }}
                              >
                                <LockResetIcon sx={{ fontSize: '17px' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete user" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(user.id)}
                                sx={{ color: '#f87171', borderRadius: '8px', '&:hover': { background: '#fee2e2', color: '#dc2626' } }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: '17px' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ borderBottom: 'none' }} />
                        <TableCell sx={{ borderBottom: 'none' }} />
                        <TableCell sx={{ borderBottom: 'none' }} />
                        <TableCell sx={{ borderBottom: 'none' }} />
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{
          height: 56,
          px: '24px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#fafbff',
          flexShrink: 0,
        }}>
          <IconButton
            size="small"
            disabled={page <= 1}
            onClick={() => fetchPage(page - 1)}
            sx={{
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              color: page <= 1 ? '#cbd5e1' : '#64748b',
              '&:hover': { background: '#f1f5f9', borderColor: '#cbd5e1' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '16px' }} />
          </IconButton>

          <Box sx={{ display: 'flex', gap: '4px' }}>
            {pageNumbers.map((p, i) => (
              <CPageButton
                key={i}
                disabled={p === '...'}
                onClick={() => typeof p === 'number' && fetchPage(p)}
                sx={p === page ? {
                  border: '1px solid #3b82f6',
                  color: '#3b82f6',
                  background: '#eff6ff',
                  fontWeight: 700,
                } : {}}
              >
                {p}
              </CPageButton>
            ))}
          </Box>

          <IconButton
            size="small"
            disabled={page >= total_pages}
            onClick={() => fetchPage(page + 1)}
            sx={{
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              color: page >= total_pages ? '#cbd5e1' : '#64748b',
              '&:hover': { background: '#f1f5f9', borderColor: '#cbd5e1' },
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
      </Paper>

      <UserModal open={openNewUser} data={userData} mode={mode} handleClose={handleCloseNewUser} />

      {/* Reset Password Confirmation */}
      <Dialog
        open={!!resetTargetId}
        onClose={() => setResetTargetId(null)}
        PaperProps={{ sx: { borderRadius: '14px', px: 1, py: 0.5, minWidth: 360 } }}
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', pb: 1 }}>
          Reset Password
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#475569' }}>
            Are you sure you want to reset this user's password to <strong>123456</strong>?
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mt: 1 }}>
            The user will need to change it after logging in.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setResetTargetId(null)}
            sx={{ textTransform: 'none', color: '#64748b', borderRadius: '8px', border: '1px solid #e2e8f0', px: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReset}
            variant="contained"
            sx={{
              textTransform: 'none',
              background: '#f59e0b',
              borderRadius: '8px',
              px: 2,
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { background: '#d97706', boxShadow: 'none' },
            }}
          >
            Yes, Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default User;
